import React, { useEffect, useContext, useState } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Flex } from 'components/shared/flex'
import { Form, useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { DateField, InputField, SelectField, TimePickerField } from 'components/shared/input'
import { A4 } from 'components/shared/print'
import { getDaysArray, selectYears, setPageSize } from 'utils/util'
import moment from 'moment'
import styled from 'styled-components'
import { ContentBody, ContentHeader } from 'components/shared/content'
import { Alert, Button, ButtonGroup, CloseButton, Dropdown, DropdownButton, OverlayTrigger, Table, ToggleButton, Tooltip } from 'react-bootstrap'
import {
    useFetchMaintenanceLedgersApi,
    usePostMaintenanceLedgerApi,
    usePostMaintenanceLedgerItemsApi,
    usePutMaintenanceLedgerApi,
} from 'api/maintenance_ledger'
import { MaintenanceLedger, MaintenanceLedgerForm, MaintenanceLedgerSearchForm } from 'entities/maintenance_ledger'
import { FixedFooter, ModalButton } from 'components/shared/widget'
import * as holiday_jp from '@holiday-jp/holiday_jp'
import {
    MaintenanceLedgerItem,
    MaintenanceLedgerItemBulkForm,
    MaintenanceLedgerItemForm,
    MaintenanceLedgerItemSearchForm,
} from 'entities/maintenance_ledger_item'
import { useFetchMaintenanceLedgerItemsApi } from 'api/maintenance_ledger_item'
import { ThemeContext } from 'contexts/theme_context'
import DatePicker from 'react-datepicker'
import { LedgerFooter, LedgerHeader } from 'components/ledger/ledger_componet'

type Props = {
    history: H.History
}

const Th = styled.th`
    border: solid 1px black;
    padding: 5px;
    text-align: center;
    min-width: 30px;
`

const Td = styled.td`
    border: solid 1px black;
    padding: 10px 5px;
    text-align: center;
`

const MaintenanceLedgerPage = ({ history }: Props) => {
    const globalState = useContext(GlobalStateContext)
    const theme = useContext(ThemeContext)
    const initDate = { start: moment().startOf('month').format('YYYY-MM-DD'), end: moment().endOf('month').format('YYYY-MM-DD') }
    const periodForm = useForm<{ year: string; month: string; day: string }>({
        year: moment().format('YYYY'),
        month: moment().format('MM'),
        day: moment().format('DD'),
    })
    const query = useQuery<{ equipmentId?: string; equipmentName?: string; equipmentNumber?: string; equipmentAlias?: string }>()
    const ledgersSearchForm = useForm<MaintenanceLedgerSearchForm>({ ...query })
    const ledgersApi = useFetchMaintenanceLedgersApi(ledgersSearchForm)
    const newLedgerForm = useForm<MaintenanceLedgerForm>({ equipmentId: query.equipmentId })
    const newLedgerApi = usePostMaintenanceLedgerApi()
    const editLedgerForm = useForm<MaintenanceLedgerForm>({})
    const editLedgerApi = usePutMaintenanceLedgerApi()
    const [currentLedger, setCurrentLedger] = useState<MaintenanceLedger>({})
    const ledgerItemForm = useForm<MaintenanceLedgerItemBulkForm>({
        items: [],
        startTime: moment().format('HH:mm'),
        endTime: moment().add('minute', 10).format('HH:mm'),
    })
    const createItemsApi = usePostMaintenanceLedgerItemsApi()
    const ledgerItemSearchForm = useForm<MaintenanceLedgerItemSearchForm>({ dateAfter: initDate.start, dateBefore: initDate.end })
    const ledgerItemsApi = useFetchMaintenanceLedgerItemsApi(ledgerItemSearchForm)
    const [currentState, setCurrentState] = useState<'input' | 'print'>('input')

    useEffect(() => {
        ledgersApi.execute()
        setPageSize('landscape')
    }, [])

    useEffectSkipFirst(() => {
        ledgerItemSearchForm.updateObject('maintenanceLedgerId', currentLedger.id)
        editLedgerForm.set(currentLedger)
    }, [currentLedger])

    useEffectSkipFirst(() => {
        globalState.setLoading(ledgersApi.loading)
        if (ledgersApi.isSuccess()) {
            const latest = ledgersApi.response.results.sort((a, b) => (a.id! > b.id! ? -1 : 1))[0]
            if (latest) {
                setCurrentLedger(latest)
            }
        }
    }, [ledgersApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(newLedgerApi.loading)
        if (newLedgerApi.isSuccess()) {
            ledgersApi.execute()
        }
    }, [newLedgerApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(createItemsApi.loading)
        if (createItemsApi.isSuccess()) {
            ledgerItemsApi.execute()
        }
    }, [createItemsApi.loading])

    useEffectSkipFirst(() => {
        ledgerItemSearchForm.update((f) => {
            const date = moment(`${periodForm.object.year}-${periodForm.object.month}-1`)
            f.dateAfter = date.format('YYYY-MM-DD')
            f.dateBefore = date.endOf('month').format('YYYY-MM-DD')
        })
    }, [periodForm.object])

    const getMaxPageNumber = () => {
        return Math.max(
            currentLedger.dailyPage!,
            currentLedger.weeklyPage!,
            currentLedger.monthlyPage!,
            currentLedger.quarterlyPage!,
            currentLedger.yearlyPage!,
            currentLedger.notePage!
        )
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(editLedgerApi.loading)
        if (editLedgerApi.isSuccess()) {
            ledgersApi.execute()
        }
    }, [editLedgerApi.loading])

    return (
        <>
            <ContentHeader
                className="no-print"
                headerTitle="検査機器保守管理作業日誌"
                left={
                    <>
                        <ModalButton
                            modalProps={{ size: 'lg' }}
                            openButton={<Button variant="success">新しいバージョンの登録</Button>}
                            modalHeader={<>保守管理作業日誌のバージョン登録</>}
                            modalBody={
                                <div>
                                    <InputField form={newLedgerForm} attr="version" apiError={newLedgerApi.apiError} label="バージョン番号" />
                                    <DateField form={newLedgerForm} attr="revisionDate" apiError={newLedgerApi.apiError} label="改定日" />
                                    {[
                                        { label: '毎日チェックする項目', attr: 'dailyItemsList', color: '#00a2', pageAttr: 'dailyPage' },
                                        { label: '毎週チェックする項目', attr: 'weeklyItemsList', color: '#a0a2', pageAttr: 'weeklyPage' },
                                        { label: '毎月チェックする項目', attr: 'monthlyItemsList', color: '#0a02', pageAttr: 'monthlyPage' },
                                        {
                                            label: '3ヶ月に1回チェックする項目',
                                            attr: 'quarterlyItemsList',
                                            color: '#a002',
                                            pageAttr: 'quarterlyPage',
                                            baseMonthAttr: 'quarterlyBaseMonth',
                                        },
                                        {
                                            label: '年1回チェックする項目',
                                            attr: 'yearlyItemsList',
                                            color: '#aa02',
                                            pageAttr: 'yearlyPage',
                                            baseMonthAttr: 'yearlyBaseMonth',
                                        },
                                        { label: '必要時にチェックする項目', attr: 'optionalItemsList', color: '#0aa2', pageAttr: 'optionalPage' },
                                    ].map((item) => {
                                        return (
                                            <div key={item.attr} style={{ marginBottom: 20 }}>
                                                <div style={{ padding: 10, backgroundColor: item.color }}>
                                                    <Flex alignItems="center">
                                                        <div>{item.label}</div>
                                                        <div style={{ width: 120 }}>
                                                            <InputField
                                                                form={newLedgerForm}
                                                                attr={item.pageAttr as keyof MaintenanceLedgerForm}
                                                                type="number"
                                                                placeholder="配置ページ"
                                                                style={{ marginBottom: 0 }}
                                                            />
                                                        </div>
                                                        {item.baseMonthAttr && (
                                                            <div style={{ width: 120 }}>
                                                                <InputField
                                                                    form={newLedgerForm}
                                                                    attr={item.baseMonthAttr as keyof MaintenanceLedgerForm}
                                                                    type="number"
                                                                    placeholder="表示基準月"
                                                                    style={{ marginBottom: 0 }}
                                                                />
                                                            </div>
                                                        )}
                                                    </Flex>

                                                    <div style={{ padding: 10 }}>
                                                        {(newLedgerForm.object[item.attr as keyof MaintenanceLedgerForm] as (string | undefined)[])?.map(
                                                            (_, index) => {
                                                                return (
                                                                    <Flex key={item.attr + index}>
                                                                        <InputField
                                                                            form={newLedgerForm}
                                                                            attr={[item.attr, index]}
                                                                            apiError={newLedgerApi.apiError}
                                                                            placeholder="項目名を入力"
                                                                        />
                                                                        <CloseButton
                                                                            style={{ marginTop: 6 }}
                                                                            onClick={() => {
                                                                                newLedgerForm.update((f) => {
                                                                                    const list = f[item.attr as keyof MaintenanceLedgerForm] as Array<any>
                                                                                    list.splice(index, 1)
                                                                                })
                                                                            }}
                                                                        />
                                                                    </Flex>
                                                                )
                                                            }
                                                        )}
                                                        <Button
                                                            style={{ width: '100%' }}
                                                            variant="secondary"
                                                            disabled={(newLedgerForm.object[item.attr as keyof MaintenanceLedgerForm] as string[])?.some(
                                                                (i) => !i
                                                            )}
                                                            onClick={() => {
                                                                newLedgerForm.update((f) => {
                                                                    if (!f[item.attr as keyof MaintenanceLedgerForm]) {
                                                                        // @ts-ignore
                                                                        f[item.attr as keyof MaintenanceLedgerForm] = []
                                                                    }
                                                                    if (
                                                                        !(f[item.attr as keyof MaintenanceLedgerForm] as string[]).some((i) => i === undefined)
                                                                    ) {
                                                                        // @ts-ignore
                                                                        f[item.attr as keyof MaintenanceLedgerForm].push(undefined)
                                                                    }
                                                                })
                                                            }}
                                                        >
                                                            + 項目追加
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}

                                    <InputField form={newLedgerForm} attr={'notePage'} type="number" label="備考欄 配置ページ" style={{ marginBottom: 0 }} />
                                    {/* <InputField form={newLedgerForm} attr="" apiError={newLedgerApi.apiError} label="" required /> */}
                                </div>
                            }
                            submitButton={
                                <Button
                                    variant="success"
                                    onClick={() => {
                                        newLedgerApi.execute(newLedgerForm)
                                    }}
                                >
                                    登録
                                </Button>
                            }
                        />
                        {currentLedger.id && (
                            <ModalButton
                                modalHeader={<div>ページ番号の編集</div>}
                                modalBody={
                                    <div>
                                        <Flex>
                                            <InputField form={editLedgerForm} attr="dailyPage" label="Dailyページ番号" type="number" />
                                            <InputField form={editLedgerForm} attr="weeklyPage" label="Weeklyページ番号" type="number" />
                                        </Flex>
                                        <Flex>
                                            <InputField form={editLedgerForm} attr="monthlyPage" label="Monthlyページ番号" type="number" />
                                            <InputField form={editLedgerForm} attr="quarterlyPage" label="Quarterlyページ番号" type="number" />
                                        </Flex>
                                        <Flex>
                                            <InputField form={editLedgerForm} attr="yearlyPage" label="Yearlyページ番号" type="number" />
                                            <InputField form={editLedgerForm} attr="optionalPage" label="optionalページ番号" type="number" />
                                        </Flex>
                                        <Flex>
                                            <InputField form={editLedgerForm} attr="notePage" label="備考欄ページ番号" type="number" />
                                        </Flex>
                                        <Flex style={{ marginTop: 30 }}>
                                            <InputField
                                                form={editLedgerForm}
                                                attr="quarterlyBaseMonth"
                                                label="4半期確認の基準月"
                                                type="number"
                                                min={1}
                                                max={12}
                                            />
                                            <InputField form={editLedgerForm} attr="yearlyBaseMonth" label="年1確認の基準月" type="number" min={1} max={12} />
                                        </Flex>
                                    </div>
                                }
                                submitButton={
                                    <Button
                                        variant="success"
                                        onClick={() => {
                                            editLedgerApi.execute(editLedgerForm)
                                        }}
                                    >
                                        更新
                                    </Button>
                                }
                                openButton={<Button>レイアウト編集</Button>}
                            />
                        )}

                        {ledgersApi.response.results.length > 0 && (
                            <DropdownButton
                                variant="primary"
                                as={ButtonGroup}
                                id={`dropdown`}
                                title={<a style={{ fontWeight: 'bold', marginRight: 3, color: 'white' }}>{`ver. ${currentLedger.version}`}</a>}
                                className=""
                                size="sm"
                            >
                                {ledgersApi.response.results
                                    .sort((a, b) => (a.id! > b.id! ? -1 : 1))
                                    .map((ledger) => {
                                        return (
                                            <Dropdown.Item
                                                active={ledger.id === currentLedger.id}
                                                onClick={() => setCurrentLedger(ledger)}
                                                key={ledger.id}
                                                eventKey="1"
                                            >{`ver.${ledger.version} (ID: ${ledger.id})`}</Dropdown.Item>
                                        )
                                    })}
                            </DropdownButton>
                        )}
                    </>
                }
                right={
                    <Flex style={{ margin: 20 }}>
                        <div style={{ marginRight: 10, width: 120 }}>
                            <SelectField className="" form={periodForm} attr="year" selectItems={selectYears()} />
                        </div>
                        <div style={{ width: 80 }}>
                            <SelectField
                                className=""
                                form={periodForm}
                                attr="month"
                                selectItems={Array(12)
                                    .fill(0)
                                    .map((_, m) => ({ label: `${m + 1}月`, value: ('0' + (m + 1)).slice(-2) }))}
                            />
                        </div>
                    </Flex>
                }
            ></ContentHeader>
            <div className="no-print">
                <Flex justifyContent="space-between" alignItems="center">
                    <div style={{ fontSize: 12 }}>
                        <div>
                            衛生No: <span style={{ fontWeight: 'bold' }}>{query.equipmentAlias}</span>
                        </div>
                        <div>
                            機器名: <span style={{ fontWeight: 'bold' }}>{query.equipmentName}</span>
                        </div>
                        <div>
                            型番: <span style={{ fontWeight: 'bold' }}>{query.equipmentNumber}</span>
                        </div>
                    </div>
                    <Flex alignItems="center">
                        <div>
                            <ButtonGroup>
                                <ToggleButton
                                    id={`radio-input`}
                                    type="radio"
                                    variant={'outline-primary'}
                                    name="radio"
                                    value={'input'}
                                    checked={currentState === 'input'}
                                    onChange={(e) => setCurrentState('input')}
                                >
                                    入力
                                </ToggleButton>
                                <ToggleButton
                                    id={`radio-print`}
                                    type="radio"
                                    variant={'outline-primary'}
                                    name="radio"
                                    value={'print'}
                                    checked={currentState === 'print'}
                                    onChange={(e) => setCurrentState('print')}
                                >
                                    印刷
                                </ToggleButton>
                            </ButtonGroup>
                        </div>
                        <div>改定日: {currentLedger.revisionDate}</div>
                    </Flex>
                </Flex>
            </div>
            {currentState === 'input' ? (
                <>
                    <ContentBody style={{ marginBottom: 100 }}>
                        {ledgersApi.response.results.length > 0 && currentLedger.id ? (
                            <div>
                                {/* ################# Start daily */}
                                {currentLedger.dailyItems && (
                                    <div
                                        style={{ padding: 10, backgroundColor: 'white', overflowX: 'scroll', margin: 10, marginBottom: 30, borderRadius: 5 }}
                                        className="shadow"
                                    >
                                        <Flex alignItems="center" style={{ marginBottom: 10 }}>
                                            <div style={{ fontWeight: 'bold', fontSize: 16 }}>毎日チェックする項目</div>
                                        </Flex>
                                        <Table bordered>
                                            <thead>
                                                <tr>
                                                    <th style={{ minWidth: 200 }}>項目名</th>
                                                    {getDaysArray(Number(periodForm.object.year), Number(periodForm.object.month)).map((day, index) => {
                                                        const date = new Date(Number(periodForm.object.year), Number(periodForm.object.month) - 1, day)
                                                        const isHoliday = holiday_jp.isHoliday(date) || date.getDay() === 0 || date.getDay() === 6
                                                        return (
                                                            <th
                                                                key={'daily-head-' + day}
                                                                style={{ minWidth: 40, textAlign: 'center', color: isHoliday ? theme.danger : 'black' }}
                                                            >
                                                                {day}
                                                            </th>
                                                        )
                                                    })}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentLedger.dailyItems?.split(',').map((item, index) => {
                                                    return (
                                                        <tr key={'daily-item-' + index}>
                                                            <td>{item}</td>

                                                            {getDaysArray(Number(periodForm.object.year), Number(periodForm.object.month)).map((day, index) => {
                                                                const date = new Date(Number(periodForm.object.year), Number(periodForm.object.month) - 1, day)
                                                                const isHoliday = holiday_jp.isHoliday(date) || date.getDay() === 0 || date.getDay() === 6
                                                                const isFuture = new Date() < date
                                                                const isBeforeCreatedAt = moment(currentLedger.revisionDate).toDate() > date
                                                                const strDate = moment(date).format('YYYY-MM-DD')
                                                                let ledgerItemIndex = ledgerItemForm.object.items.findIndex(
                                                                    (li) => li.date === strDate && li.name === item
                                                                )
                                                                let ledgerItem: MaintenanceLedgerItemForm = {}
                                                                if (ledgerItemIndex < 0) {
                                                                    ledgerItem = { name: item, date: strDate, kind: 'daily' }
                                                                    ledgerItemIndex = ledgerItemForm.object.items.length
                                                                } else {
                                                                    ledgerItem = ledgerItemForm.object.items[ledgerItemIndex]
                                                                }
                                                                const existItem = ledgerItemsApi.response.results.find(
                                                                    (li) => li.date === strDate && li.name === item && li.kind === 'daily'
                                                                )
                                                                return (
                                                                    <td key={'daily-body-' + day} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                                        {isFuture ? (
                                                                            '-'
                                                                        ) : isBeforeCreatedAt ? (
                                                                            'x'
                                                                        ) : existItem ? (
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id={`daily-info`}>
                                                                                        <table style={{ textAlign: 'left' }}>
                                                                                            <tbody>
                                                                                                <tr>
                                                                                                    <th style={{ width: 80 }}>ID</th>
                                                                                                    <td>{existItem.id}</td>
                                                                                                </tr>
                                                                                                <tr>
                                                                                                    <th style={{ width: 80 }}>確認者</th>
                                                                                                    <td>{existItem.user?.fullName}</td>
                                                                                                </tr>
                                                                                                <tr>
                                                                                                    <th>確認時刻</th>
                                                                                                    <td>
                                                                                                        {moment(
                                                                                                            `${existItem.date} ${existItem.startTime}`
                                                                                                        ).format('M月D日 HH:mm')}{' '}
                                                                                                        ~{' '}
                                                                                                        {moment(
                                                                                                            `${existItem.date} ${existItem.startTime}`
                                                                                                        ).format('HH:mm')}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        fontSize: 12,
                                                                                        fontWeight: 'bold',
                                                                                        color: `${existItem.isOk ? theme.success : theme.danger}`,
                                                                                        cursor: 'default',
                                                                                    }}
                                                                                >
                                                                                    {existItem.isOk ? 'OK' : 'NG'}
                                                                                </div>
                                                                            </OverlayTrigger>
                                                                        ) : ledgerItem.isOk === undefined ? (
                                                                            <Button
                                                                                style={{ padding: 0, width: 25, height: 25, lineHeight: 0, fontSize: 10 }}
                                                                                variant="secondary"
                                                                                onClick={() => {
                                                                                    ledgerItem.isOk = true
                                                                                    ledgerItemForm.update((f) => {
                                                                                        f.items[ledgerItemIndex] = ledgerItem
                                                                                    })
                                                                                }}
                                                                            >
                                                                                -
                                                                            </Button>
                                                                        ) : ledgerItem.isOk ? (
                                                                            <Button
                                                                                style={{ padding: 0, width: 25, height: 25, lineHeight: 0, fontSize: 10 }}
                                                                                variant="success"
                                                                                onClick={() => {
                                                                                    ledgerItem.isOk = false
                                                                                    ledgerItemForm.update((f) => {
                                                                                        f.items[ledgerItemIndex] = ledgerItem
                                                                                    })
                                                                                }}
                                                                            >
                                                                                OK
                                                                            </Button>
                                                                        ) : (
                                                                            <Button
                                                                                style={{ padding: 0, width: 25, height: 25, lineHeight: 0, fontSize: 10 }}
                                                                                variant="danger"
                                                                                onClick={() => {
                                                                                    ledgerItemForm.update((f) => {
                                                                                        f.items.splice(ledgerItemIndex, 1)
                                                                                    })
                                                                                }}
                                                                            >
                                                                                NG
                                                                            </Button>
                                                                        )}
                                                                    </td>
                                                                )
                                                            })}
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                                {/* ################# End daily */}

                                {currentLedger.weeklyItems && (
                                    <MoreWeeklyInputForm
                                        ledgerItems={ledgerItemsApi.response.results}
                                        itemNames={currentLedger.weeklyItems?.split(',')}
                                        ledgerItemForm={ledgerItemForm}
                                        kind="weekly"
                                        currentLedger={currentLedger}
                                    />
                                )}
                                {currentLedger.optionalItems && (
                                    <MoreWeeklyInputForm
                                        ledgerItems={ledgerItemsApi.response.results}
                                        itemNames={currentLedger.optionalItems?.split(',')}
                                        ledgerItemForm={ledgerItemForm}
                                        kind="optional"
                                        currentLedger={currentLedger}
                                    />
                                )}
                                <Flex>
                                    {currentLedger.monthlyItems && (
                                        <MoreWeeklyInputForm
                                            ledgerItems={ledgerItemsApi.response.results}
                                            itemNames={currentLedger.monthlyItems?.split(',')}
                                            ledgerItemForm={ledgerItemForm}
                                            kind="monthly"
                                            currentLedger={currentLedger}
                                        />
                                    )}
                                    {currentLedger.quarterlyItems &&
                                        !!currentLedger.quarterlyBaseMonth &&
                                        [
                                            [1, 4, 7, 10],
                                            [2, 5, 8, 11],
                                            [3, 6, 9, 12],
                                        ]
                                            .find((l) => l.includes(Number(periodForm.object.month)))
                                            ?.includes(currentLedger.quarterlyBaseMonth) && (
                                            <MoreWeeklyInputForm
                                                ledgerItems={ledgerItemsApi.response.results}
                                                itemNames={currentLedger.quarterlyItems?.split(',')}
                                                ledgerItemForm={ledgerItemForm}
                                                kind="quarterly"
                                                currentLedger={currentLedger}
                                            />
                                        )}
                                    {currentLedger.yearlyItems && currentLedger.yearlyBaseMonth === Number(periodForm.object.month) && (
                                        <MoreWeeklyInputForm
                                            ledgerItems={ledgerItemsApi.response.results}
                                            itemNames={currentLedger.yearlyItems?.split(',')}
                                            ledgerItemForm={ledgerItemForm}
                                            kind="yearly"
                                            currentLedger={currentLedger}
                                        />
                                    )}
                                </Flex>

                                <div style={{ padding: 10, margin: 10, backgroundColor: 'white' }} className="shadow">
                                    <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 10 }}>備考欄</div>
                                    <div style={{ marginBottom: 20 }}>
                                        {ledgerItemsApi.response.results
                                            .filter((li) => li.kind === 'note')
                                            .map((li, index) => {
                                                if (!li.note) {
                                                    return
                                                }
                                                return (
                                                    <div key={'note-' + index} style={{ marginBottom: 10 }}>
                                                        <Flex style={{ fontSize: 11 }}>
                                                            <div>{li.date}</div>
                                                            <div>{li.user?.fullName}</div>
                                                            <div style={{ fontSize: 10 }}>（ID : {li.id}）</div>
                                                        </Flex>
                                                        <div>{li.note}</div>
                                                    </div>
                                                )
                                            })}
                                    </div>
                                    {ledgerItemForm.object.items.map((li, index) => {
                                        if (li.kind !== 'note') {
                                            return
                                        }
                                        return (
                                            <Flex key={'note-form-' + index}>
                                                <InputField form={ledgerItemForm} attr={['items', index, 'note']} placeholder="備考" />
                                                <DateField form={ledgerItemForm} attr={['items', index, 'date']} />
                                                <CloseButton
                                                    style={{ marginTop: 6 }}
                                                    onClick={() => {
                                                        const fIndex = ledgerItemForm.object.items.findIndex(
                                                            (li) => li.note === ledgerItemForm.getValue(['items', index, 'note'])
                                                        )
                                                        ledgerItemForm.update((f) => {
                                                            f.items.splice(fIndex, 1)
                                                        })
                                                    }}
                                                ></CloseButton>
                                            </Flex>
                                        )
                                    })}
                                    <Button
                                        variant="secondary"
                                        disabled={
                                            ledgerItemForm.object.items.filter((li) => li.kind === 'note').length > 0 &&
                                            ledgerItemForm.object.items.every((li) => li.kind === 'note' && !li.note)
                                        }
                                        onClick={() => {
                                            ledgerItemForm.update((f) => {
                                                f.items.push({
                                                    date: moment(`${periodForm.object.year}-${periodForm.object.month}-${periodForm.object.day}`).format(
                                                        'YYYY-MM-DD'
                                                    ),
                                                    kind: 'note',
                                                    name: '備考',
                                                    isOk: true,
                                                })
                                            })
                                        }}
                                    >
                                        + 備考欄追加
                                    </Button>
                                </div>
                            </div>
                        ) : !ledgersApi.loading ? (
                            <div style={{ textAlign: 'center', padding: 70, backgroundColor: 'white', marginTop: 20 }}>
                                最新バージョンの作業日誌を登録してください
                            </div>
                        ) : (
                            <div></div>
                        )}
                    </ContentBody>
                    <FixedFooter open={ledgerItemForm.object.items.length > 0}>
                        <Flex alignItems="center" style={{ width: '100%' }}>
                            <ModalButton
                                openButton={<Button variant="success">時間を入力して保存</Button>}
                                modalHeader={<>時間を入力してください</>}
                                modalBody={
                                    <div>
                                        <Flex>
                                            <TimePickerField form={ledgerItemForm} attr="startTime" required label="開始時刻" />
                                            <TimePickerField form={ledgerItemForm} attr="endTime" required label="終了時刻" />
                                        </Flex>
                                    </div>
                                }
                                submitButton={
                                    <Button
                                        variant="success"
                                        onClick={() => {
                                            createItemsApi.execute(currentLedger.id!, ledgerItemForm)
                                        }}
                                    >
                                        保存
                                    </Button>
                                }
                            />
                            <Alert variant="warning" style={{ padding: '8px 20px', margin: 0 }}>
                                まだ保存されていません
                            </Alert>
                        </Flex>
                    </FixedFooter>
                </>
            ) : (
                <div>
                    <Flex className="no-print" alignItems="flex-end">
                        <div style={{ marginTop: 20 }}>
                            <Button
                                style={{ width: 200 }}
                                onClick={() => {
                                    window.print()
                                }}
                            >
                                印刷プレビュー
                            </Button>
                        </div>
                    </Flex>
                    {new Array(getMaxPageNumber()).fill(0).map((_, pageIndex) => {
                        const currentCategory = currentLedger.equipment?.equipmentCategory
                        return (
                            <A4 padding={0} landscape key={'page-' + pageIndex}>
                                <div style={{ padding: 30, marginTop: 15 }}>
                                    <div>
                                        <LedgerHeader
                                            headerTitle={`${currentCategory?.name}保守管理作業日誌`}
                                            recordTypeNum={currentCategory?.code}
                                            originalDocument={currentLedger.equipment?.originalDocument}
                                            versionNum={currentLedger.version}
                                            timestamp={moment().format('YYYYMMDDHHmm')}
                                        ></LedgerHeader>

                                        <div style={{ margin: '10px 20px', fontSize: 13, marginBottom: 15 }}>
                                            <div>
                                                {query.equipmentName} / {`${query.equipmentNumber} (衛生${query.equipmentAlias})`}
                                            </div>
                                        </div>

                                        {currentLedger.dailyItems && pageIndex + 1 === currentLedger.dailyPage && (
                                            <table style={{ border: '2px solid black', fontSize: 10, width: '100%', marginBottom: 20, tableLayout: 'fixed' }}>
                                                <thead>
                                                    <tr>
                                                        <Th colSpan={5}>{periodForm.object.month}月</Th>
                                                        <Th colSpan={getDaysArray(Number(periodForm.object.year), Number(periodForm.object.month)).length}>
                                                            結果・日付・開始時間・終了時間
                                                        </Th>
                                                    </tr>
                                                    <tr>
                                                        <Th colSpan={2} style={{ minWidth: 65 }}>
                                                            点検頻度
                                                        </Th>
                                                        <Th colSpan={3} style={{ textAlign: 'center', minWidth: 65 }}>
                                                            点検項目
                                                        </Th>
                                                        {getDaysArray(Number(periodForm.object.year), Number(periodForm.object.month)).map((day, index) => {
                                                            const date = new Date(Number(periodForm.object.year), Number(periodForm.object.month) - 1, day)
                                                            const isHoliday = holiday_jp.isHoliday(date) || date.getDay() === 0 || date.getDay() === 6
                                                            return (
                                                                <Th key={'daily-head-' + day} style={{ minWidth: 25, width: 30, textAlign: 'center' }}>
                                                                    {day}
                                                                </Th>
                                                            )
                                                        })}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {[...currentLedger.dailyItems!.split(','), '点検開始・終了時間', '作業担当者'].map((item, index) => {
                                                        const isItem = !(item === '点検開始・終了時間' || item === '作業担当者')
                                                        return (
                                                            <tr key={'daily-item-' + index}>
                                                                {index === 0 && isItem && (
                                                                    <Td colSpan={2} rowSpan={currentLedger.dailyItems?.split(',').length}>
                                                                        毎日
                                                                    </Td>
                                                                )}
                                                                <Td colSpan={isItem ? 3 : 5} style={{ textAlign: 'left', width: 50 }}>
                                                                    {item}
                                                                </Td>

                                                                {getDaysArray(Number(periodForm.object.year), Number(periodForm.object.month)).map(
                                                                    (day, index) => {
                                                                        const date = new Date(
                                                                            Number(periodForm.object.year),
                                                                            Number(periodForm.object.month) - 1,
                                                                            day
                                                                        )
                                                                        const today = new Date()
                                                                        const isHoliday =
                                                                            holiday_jp.isHoliday(date) || date.getDay() === 0 || date.getDay() === 6
                                                                        const isFuture = new Date() < date
                                                                        const isBeforeCreatedAt = moment(currentLedger.revisionDate).toDate() > date
                                                                        const strDate = moment(date).format('YYYY-MM-DD')
                                                                        const firstItem = currentLedger.dailyItems!.split(',')[0]
                                                                        const existItem = ledgerItemsApi.response.results.find(
                                                                            (li) =>
                                                                                li.date === strDate &&
                                                                                li.name === (isItem ? item : firstItem) &&
                                                                                li.kind === 'daily'
                                                                        )
                                                                        return (
                                                                            <Td
                                                                                key={'print-daily-body-' + day}
                                                                                style={{
                                                                                    ...{
                                                                                        textAlign: 'center',
                                                                                        verticalAlign: 'middle',
                                                                                        padding: isItem ? 'inherit' : 0,
                                                                                    },
                                                                                }}
                                                                            >
                                                                                {isBeforeCreatedAt ? (
                                                                                    '-'
                                                                                ) : isHoliday ? (
                                                                                    '休'
                                                                                ) : !existItem && date < today ? (
                                                                                    'N/A'
                                                                                ) : !existItem ? (
                                                                                    ''
                                                                                ) : isItem ? (
                                                                                    existItem?.isOk ? (
                                                                                        'OK'
                                                                                    ) : (
                                                                                        'NG'
                                                                                    )
                                                                                ) : item === '点検開始・終了時間' ? (
                                                                                    <div style={{ transform: 'scale(80%)' }}>
                                                                                        <div>
                                                                                            {moment(`${strDate} ${existItem?.startTime}`).format('HH:mm')}
                                                                                        </div>
                                                                                        <div>|</div>
                                                                                        <div>{moment(`${strDate} ${existItem?.endTime}`).format('HH:mm')}</div>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div style={{ transform: 'scale(70%)', whiteSpace: 'nowrap' }}>
                                                                                        {existItem?.user?.fullName?.split(' ')[0]}
                                                                                        <br />
                                                                                        {existItem?.user?.fullName?.split(' ')[1]}
                                                                                    </div>
                                                                                )}
                                                                            </Td>
                                                                        )
                                                                    }
                                                                )}
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        )}

                                        {currentLedger.weeklyItems && pageIndex + 1 === currentLedger.weeklyPage && (
                                            <MoreWeeklyPrintView
                                                ledgerItems={ledgerItemsApi.response.results}
                                                itemNames={currentLedger.weeklyItems?.split(',')}
                                                ledgerItemForm={ledgerItemForm}
                                                kind="weekly"
                                                currentLedger={currentLedger}
                                            />
                                        )}
                                        {currentLedger.optionalItems && pageIndex + 1 === currentLedger.optionalPage && (
                                            <MoreWeeklyPrintView
                                                ledgerItems={ledgerItemsApi.response.results}
                                                itemNames={currentLedger.optionalItems?.split(',')}
                                                ledgerItemForm={ledgerItemForm}
                                                kind="optional"
                                                currentLedger={currentLedger}
                                            />
                                        )}
                                        <Flex flexWrap="wrap">
                                            {currentLedger.monthlyItems && pageIndex + 1 === currentLedger.monthlyPage && (
                                                <MoreWeeklyPrintView
                                                    ledgerItems={ledgerItemsApi.response.results}
                                                    itemNames={currentLedger.monthlyItems?.split(',')}
                                                    ledgerItemForm={ledgerItemForm}
                                                    kind="monthly"
                                                    currentLedger={currentLedger}
                                                />
                                            )}
                                            {currentLedger.quarterlyItems &&
                                                pageIndex + 1 === currentLedger.quarterlyPage &&
                                                !!currentLedger.quarterlyBaseMonth &&
                                                [
                                                    [1, 4, 7, 10],
                                                    [2, 5, 8, 11],
                                                    [3, 6, 9, 12],
                                                ]
                                                    .find((l) => l.includes(Number(periodForm.object.month)))
                                                    ?.includes(currentLedger.quarterlyBaseMonth) && (
                                                    <MoreWeeklyPrintView
                                                        ledgerItems={ledgerItemsApi.response.results}
                                                        itemNames={currentLedger.quarterlyItems?.split(',')}
                                                        ledgerItemForm={ledgerItemForm}
                                                        kind="quarterly"
                                                        currentLedger={currentLedger}
                                                    />
                                                )}
                                            {currentLedger.yearlyItems &&
                                                pageIndex + 1 === currentLedger.yearlyPage &&
                                                currentLedger.yearlyBaseMonth === Number(periodForm.object.month) && (
                                                    <MoreWeeklyPrintView
                                                        ledgerItems={ledgerItemsApi.response.results}
                                                        itemNames={currentLedger.yearlyItems?.split(',')}
                                                        ledgerItemForm={ledgerItemForm}
                                                        kind="yearly"
                                                        currentLedger={currentLedger}
                                                    />
                                                )}
                                        </Flex>

                                        {pageIndex + 1 === currentLedger.notePage && (
                                            <div>
                                                <div style={{ fontSize: 10 }}>備考欄</div>
                                                <Flex
                                                    flexDirection="column"
                                                    style={{ border: 'solid 2px black', padding: 10, fontSize: 10, width: '100%', minHeight: 80 }}
                                                >
                                                    {(() => {
                                                        const existItem = ledgerItemsApi.response.results.filter((li) => li.kind === 'note' && li.note)
                                                        if (existItem.length === 0) {
                                                            return <div>特記事項なし</div>
                                                        } else {
                                                            return existItem.map((li, index) => {
                                                                return (
                                                                    <div key={'print-note-' + index}>
                                                                        <div>
                                                                            {li.date} {li.user?.fullName}
                                                                        </div>
                                                                        <div>{li.note}</div>
                                                                    </div>
                                                                )
                                                            })
                                                        }
                                                    })()}
                                                </Flex>
                                            </div>
                                        )}
                                    </div>

                                    <LedgerFooter page={pageIndex + 1}></LedgerFooter>
                                </div>
                            </A4>
                        )
                    })}
                </div>
            )}
        </>
    )
}

type MoreWeeklyInputFormProps = {
    kind: 'weekly' | 'optional' | 'monthly' | 'quarterly' | 'yearly'
    currentLedger: MaintenanceLedger
    ledgerItems: MaintenanceLedgerItem[]
    ledgerItemForm: Form<MaintenanceLedgerItemBulkForm>
    itemNames: string[]
}

const MoreWeeklyInputForm = (props: MoreWeeklyInputFormProps) => {
    const { kind, currentLedger, ledgerItems, ledgerItemForm, itemNames } = props
    const theme = useContext(ThemeContext)
    const getTitle = () => {
        if (kind === 'weekly') {
            return '毎週'
        } else if (kind === 'optional') {
            return '必要時'
        } else if (kind === 'monthly') {
            return '月1回'
        } else if (kind === 'quarterly') {
            return '3ヶ月に1回'
        } else if (kind === 'yearly') {
            return '年1回'
        }
    }

    const colSize = () => {
        if (kind === 'weekly' || kind === 'optional') {
            return 5
        } else {
            return 1
        }
    }
    return (
        <div
            style={{
                padding: 10,
                backgroundColor: 'white',
                margin: 10,
                marginBottom: 30,
                borderRadius: 5,
                width: kind === 'weekly' || kind === 'optional' ? 'auto' : '100%',
            }}
            className="shadow"
        >
            <div style={{ marginBottom: 5, fontWeight: 'bold', fontSize: 16 }}>{getTitle()}チェックする項目</div>
            <Table
                bordered
                style={{
                    verticalAlign: 'middle',
                }}
            >
                <thead>
                    <tr>
                        <th style={kind === 'weekly' || kind === 'optional' ? { minWidth: 400, maxWidth: 400, width: 400 } : { minWidth: 180 }}>項目名</th>
                        {new Array(colSize()).fill(0).map((_, index) => {
                            return (
                                <th key={kind + '-head-' + index} style={{ minWidth: 120, textAlign: 'center' }}>
                                    {kind === 'weekly' ? `${index + 1}週目` : kind === 'optional' ? `${index + 1}回目` : ''}
                                </th>
                            )
                        })}
                    </tr>
                </thead>
                <tbody>
                    {itemNames.map((item, index) => {
                        const contents = item.split(' ')
                        return (
                            <tr key={kind + '-item-' + index}>
                                <td>
                                    {contents.map((content, index) => {
                                        return <div key={'content-' + index}>{content}</div>
                                    })}
                                </td>

                                {new Array(colSize()).fill(0).map((_, colIndex) => {
                                    const existItem = ledgerItems.find((li) => li.kind === kind && li.name === item && li.weekIndex === colIndex)

                                    let ledgerItemIndex = ledgerItemForm.object.items.findIndex(
                                        (li) => li.name === item && li.kind === kind && li.weekIndex === colIndex
                                    )
                                    const ledgerItem = ledgerItemForm.object.items[ledgerItemIndex] ?? {
                                        name: item,
                                        kind: kind,
                                        weekIndex: colIndex,
                                    }
                                    if (ledgerItemIndex < 0) {
                                        ledgerItemIndex = ledgerItemForm.object.items.length
                                    }
                                    return (
                                        <td key={kind + '-body-' + colIndex} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                            {existItem ? (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id={`daily-info`}>
                                                            <table style={{ textAlign: 'left' }}>
                                                                <tbody>
                                                                    <tr>
                                                                        <th style={{ width: 80 }}>ID</th>
                                                                        <td>{existItem.id}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th style={{ width: 80 }}>確認者</th>
                                                                        <td>{existItem.user?.fullName}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th>確認時刻</th>
                                                                        <td>
                                                                            {moment(`${existItem.date} ${existItem.startTime}`).format('M月D日 HH:mm')} ~{' '}
                                                                            {moment(`${existItem.date} ${existItem.endTime}`).format('HH:mm')}
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <div
                                                        style={{
                                                            fontSize: 12,
                                                            fontWeight: 'bold',
                                                            color: `${existItem.isOk ? theme.success : theme.danger}`,
                                                            cursor: 'default',
                                                        }}
                                                    >
                                                        {existItem.isOk ? 'OK' : 'NG'}
                                                    </div>
                                                </OverlayTrigger>
                                            ) : (
                                                <Flex alignItems="center">
                                                    <div style={{ width: 70, marginRight: 10 }}>
                                                        <DatePicker
                                                            className={`form-control ignore-margin`}
                                                            value={ledgerItem.date ? moment(ledgerItem.date).format('MM/DD') : undefined}
                                                            onChange={(e) => {
                                                                if (e) {
                                                                    ledgerItem.isOk = true
                                                                    ledgerItem.date = moment(e.toLocaleString()).format('YYYY-MM-DD')
                                                                    ledgerItemForm.update((f) => {
                                                                        f.items[ledgerItemIndex] = ledgerItem
                                                                    })
                                                                }
                                                            }}
                                                            locale="ja"
                                                            maxDate={new Date()}
                                                        />
                                                    </div>

                                                    {ledgerItem.isOk === undefined ? (
                                                        <Button
                                                            style={{ padding: 0, width: 25, height: 25, lineHeight: 0, fontSize: 10 }}
                                                            variant="secondary"
                                                            onClick={() => {
                                                                ledgerItem.isOk = true
                                                                ledgerItemForm.update((f) => {
                                                                    f.items[ledgerItemIndex] = ledgerItem
                                                                })
                                                            }}
                                                            disabled={!ledgerItem.date}
                                                        >
                                                            -
                                                        </Button>
                                                    ) : ledgerItem.isOk ? (
                                                        <Button
                                                            style={{ padding: 0, width: 25, height: 25, lineHeight: 0, fontSize: 10 }}
                                                            variant="success"
                                                            onClick={() => {
                                                                ledgerItem.isOk = false
                                                                ledgerItemForm.update((f) => {
                                                                    f.items[ledgerItemIndex] = ledgerItem
                                                                })
                                                            }}
                                                        >
                                                            OK
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            style={{ padding: 0, width: 25, height: 25, lineHeight: 0, fontSize: 10 }}
                                                            variant="danger"
                                                            onClick={() => {
                                                                ledgerItemForm.update((f) => {
                                                                    f.items.splice(ledgerItemIndex, 1)
                                                                })
                                                            }}
                                                        >
                                                            NG
                                                        </Button>
                                                    )}
                                                </Flex>
                                            )}
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </Table>
        </div>
    )
}

const MoreWeeklyPrintView = (props: MoreWeeklyInputFormProps) => {
    const { kind, currentLedger, ledgerItems, ledgerItemForm, itemNames } = props
    const theme = useContext(ThemeContext)
    const getTitle = () => {
        if (kind === 'weekly') {
            return '毎週'
        } else if (kind === 'optional') {
            return '必要時'
        } else if (kind === 'monthly') {
            return '月1回'
        } else if (kind === 'quarterly') {
            return '3ヶ月毎'
        } else if (kind === 'yearly') {
            return '年1回'
        }
    }

    const colSize = () => {
        if (kind === 'weekly' || kind === 'optional') {
            return 5
        } else {
            return 1
        }
    }
    return (
        <div style={{ marginBottom: 20 }}>
            <table style={{ border: '2px solid black', fontSize: 10 }}>
                <thead>
                    <tr>
                        <Th style={{ minWidth: 60 }}>点検頻度</Th>
                        <Th style={{ textAlign: 'center', minWidth: 200 }}>点検項目</Th>
                        <Th colSpan={colSize()}>結果・日付・開始時刻・終了時刻</Th>
                    </tr>
                </thead>
                <tbody>
                    {[...itemNames].map((item, index) => {
                        const isItem = !(item === '確認者')
                        const contents = item.split(' ')
                        return (
                            <tr key={kind + '-item-' + index}>
                                {isItem && index === 0 && <Td rowSpan={itemNames.length}>{getTitle()}</Td>}
                                <Td colSpan={isItem ? 1 : 2} style={{ textAlign: 'left' }}>
                                    <div>
                                        <div>{contents}</div>
                                    </div>
                                </Td>

                                {new Array(colSize()).fill(0).map((_, colIndex) => {
                                    const existItem = ledgerItems.find((li) => li.kind === kind && li.name === item && li.weekIndex === colIndex)
                                    return (
                                        <Td
                                            key={'print-daily-body-' + colIndex}
                                            style={{ ...{ textAlign: 'center', verticalAlign: 'middle', minWidth: 150, padding: 5 } }}
                                        >
                                            <div>
                                                {existItem?.date ? (
                                                    <div>
                                                        <Flex justifyContent="center">
                                                            <div>{existItem?.isOk ? 'OK' : 'NG'}</div>
                                                            <div>({existItem?.user?.fullName})</div>
                                                        </Flex>
                                                        <div>
                                                            {moment(`${existItem.date} ${existItem?.startTime}`).format('YYYY/MM/DD HH:mm')} ~{' '}
                                                            {moment(`${existItem.date} ${existItem?.endTime}`).format('HH:mm')}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>N/A</div>
                                                )}
                                            </div>
                                        </Td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}
export default withRouter(MaintenanceLedgerPage)
